"use client"
import Link from "next/link"
import Image from "next/image"
import { useEffect, useState } from "react"
import Icon from "@/components/ui/Icon"
import { MAX_TRUST_SCORE } from "@/lib/trust/constants"

export default function ComparePage() {
  const [allListings, setAllListings] = useState<any[]>([])
  const [selected, setSelected] = useState<any[]>([])
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let savedIds: string[] = []
    try { savedIds = JSON.parse(localStorage.getItem("ze_compare") ?? "[]") } catch { savedIds = [] }

    // The picker list is just the newest 40. The BASKET must be resolved through
    // the ids endpoint instead of filtered out of that list — anything added from
    // page 2+ or from a filtered search was not in the 40 and silently vanished.
    fetch("/api/listings?pageSize=40")
      .then(r => r.json())
      .then(d => { setAllListings(d.data ?? []); setLoading(false) })
      .catch(() => setLoading(false))

    if (savedIds.length > 0) {
      fetch("/api/listings?ids=" + savedIds.map(encodeURIComponent).join(","))
        .then(r => r.json())
        .then(d => { if (Array.isArray(d?.data) && d.data.length > 0) setSelected(d.data) })
        .catch(() => {})
    }
  }, [])

  function toggle(listing: any) {
    setSelected(prev => {
      const next = prev.find(l => l.id === listing.id)
        ? prev.filter(l => l.id !== listing.id)
        : prev.length >= 3 ? prev : [...prev, listing]
      localStorage.setItem("ze_compare", JSON.stringify(next.map(l => l.id)))
      return next
    })
  }

  // The public-browse list strips the features relation; enrich the selected
  // listings with their features (via the ids path) so the comparison matrix fills.
  useEffect(() => {
    const ids = selected.map(l => l.id)
    if (ids.length === 0 || !selected.some(l => l.features === undefined)) return
    fetch("/api/listings?ids=" + ids.join(","))
      .then(r => r.json())
      .then(d => {
        const byId = new Map((d.data ?? []).map((l: any) => [l.id, l]))
        setSelected(prev => prev.map(l => (l.features === undefined && byId.get(l.id)) ? (byId.get(l.id) as any) : l))
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.map(l => l.id).join(",")])

  const query = search.toLowerCase().trim()
  const filtered = allListings.filter(l =>
    !query ||
    l.title?.toLowerCase().includes(query) ||
    l.suburb?.toLowerCase().includes(query) ||
    l.city?.toLowerCase().includes(query)
  )

  const allFeatures = [...new Set(selected.flatMap(l => (l.features ?? []).map((f: any) => f.feature)))]

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>
      <div style={{ background: "var(--navy)", padding: "48px 24px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 40, fontWeight: 700, color: "var(--cream)", marginBottom: 8 }}>Compare <em style={{ color: "var(--gold)" }}>properties</em>.</h1>
          <p style={{ color: "rgba(253,251,247,0.75)", fontSize: 15 }}>Select up to 3 properties to compare side by side.</p>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "32px 24px" }}>
        <div style={{ marginBottom: 24 }}>
          <input
            placeholder="Search by suburb, title, or city..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: "100%", padding: "12px 16px", borderRadius: 10, border: "1px solid var(--line)", fontSize: 14, fontFamily: "inherit", background: "var(--cream)", color: "var(--navy)", outline: "none", boxSizing: "border-box" }}
          />
        </div>

        {loading ? (
          <div style={{ color: "var(--muted)", fontSize: 14, marginBottom: 20 }}>Loading listings...</div>
        ) : (
          <div style={{ display: "flex", gap: 8, marginBottom: 32, flexWrap: "wrap", maxHeight: 160, overflowY: "auto" }}>
            {filtered.slice(0, 30).map(l => {
              const isSelected = !!selected.find(s => s.id === l.id)
              const disabled = !isSelected && selected.length >= 3
              return (
                <button
                  key={l.id}
                  onClick={() => !disabled && toggle(l)}
                  disabled={disabled}
                  style={{ padding: "7px 14px", borderRadius: 20, border: "1px solid var(--line)", fontSize: 12, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit", background: isSelected ? "var(--navy)" : "var(--cream)", color: isSelected ? "var(--cream)" : disabled ? "var(--muted)" : "var(--navy)", opacity: disabled ? 0.5 : 1, transition: "all 0.15s" }}
                >
                  {(l.title ?? "").substring(0, 28)}{(l.title ?? "").length > 28 ? "..." : ""} · {l.suburb}
                </button>
              )
            })}
            {filtered.length === 0 && <span style={{ fontSize: 13, color: "var(--muted)" }}>No listings match your search.</span>}
          </div>
        )}

        {selected.length > 0 ? (
          <>
          <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 14 }}>←</span> Scroll to compare <span style={{ fontSize: 14 }}>→</span>
          </div>
          <div style={{ overflowX: "auto", borderRadius: 16, border: "1px solid var(--line)", WebkitOverflowScrolling: "touch" } as React.CSSProperties}>
            <div style={{ display: "grid", gridTemplateColumns: "160px " + selected.map(() => "1fr").join(" "), gap: 0, minWidth: 520 }}>
              <div style={{ background: "var(--cream-warm)" }}>
                {["", "Price", "Location", "Type", "Bedrooms", "Bathrooms", "Floor area", "Trust Score", ...allFeatures].map((label, i) => (
                  <div key={i} style={{ padding: "14px 16px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 600, color: "var(--muted)", minHeight: i === 0 ? 180 : 48, display: "flex", alignItems: "center" }}>{label}</div>
                ))}
                {allFeatures.length === 0 && selected.length > 0 && (
                  <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--line)", fontSize: 12, color: "var(--muted)", minHeight: 48, display: "flex", alignItems: "center" }}>No features listed</div>
                )}
              </div>

              {selected.map(col => {
                const photo = col.photos?.[0]?.url
                const fallbackBg = col.priceType === "RENT" ? "linear-gradient(135deg,#3d8b5f,#1B2D54)" : "linear-gradient(135deg,#3a4f7a,#1B2D54)"
                const colFeatures = (col.features ?? []).map((f: any) => f.feature)
                return (
                  <div key={col.id} style={{ borderLeft: "1px solid var(--line)" }}>
                    <div style={{ height: 180, background: photo ? undefined : fallbackBg, position: "relative" }}>
                      {photo && <Image src={photo} alt={col.title} fill sizes="(max-width: 640px) 100vw, 400px" style={{ objectFit: "cover" }} />}
                      <button onClick={() => setSelected(prev => { const next = prev.filter(x => x.id !== col.id); localStorage.setItem("ze_compare", JSON.stringify(next.map(l => l.id))); return next })} style={{ position: "absolute", top: 8, right: 8, background: "rgba(253,251,247,0.9)", border: "none", borderRadius: "50%", width: 28, height: 28, cursor: "pointer", display: "grid", placeItems: "center" }}><Icon name="X" size={12} /></button>
                      {col.isFeatured && <span style={{ position: "absolute", top: 8, left: 8, background: "#C8963E", color: "#1B2D54", fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 4, textTransform: "uppercase" }}>Featured</span>}
                    </div>
                    {[
                      col.priceType === "RENT" ? `$${col.price?.toLocaleString()}/mo` : `$${col.price?.toLocaleString()}`,
                      `${col.suburb}, ${col.city}`,
                      col.propertyType ?? "-",
                      col.beds ? col.beds + " beds" : "-",
                      col.baths ? col.baths + " baths" : "-",
                      col.floorArea ? col.floorArea + "m²" : "-",
                      col.trustScore ? col.trustScore.totalScore + "/" + MAX_TRUST_SCORE : "-",
                      ...allFeatures.map(f => colFeatures.includes(f) ? "✓" : "−"),
                    ].map((val, i) => (
                      <div key={i} style={{ padding: "14px 16px", borderBottom: "1px solid var(--line)", fontSize: 13, fontWeight: i === 0 ? 700 : 400, color: val === "✓" ? "var(--green)" : val === "−" ? "var(--muted)" : "var(--navy)", minHeight: 48, display: "flex", alignItems: "center" }}>{val}</div>
                    ))}
                    <div style={{ padding: "12px 16px" }}>
                      <Link href={"/listings/" + col.id} style={{ display: "block", textAlign: "center", padding: "8px", background: "var(--navy)", color: "var(--cream)", borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>View listing</Link>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          </>
        ) : (
          <div style={{ textAlign: "center", padding: 64, color: "var(--muted)", background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 16 }}>
            {loading ? "Loading listings..." : "Select properties above to compare them side by side."}
          </div>
        )}
      </div>
    </div>
  )
}
